import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { firstValueFrom, timeout } from 'rxjs';
import {
  Account,
  AccountSummary,
  ActiveSignal,
  BacktestResult,
  BacktestStatus,
  BacktestTrade,
  Bar,
  Corridor,
  DbTrade,
  EngineLog,
  EngineStatus,
  Fractal,
  Portfolio,
  Position,
  Trade,
  SwarmServiceStatus,
  PatternIntelligenceHealth,
  PatternIntelligenceDbStats,
  RunnerStatusUI,
  RunnerHeartbeat,
} from '../models';

// Re-export models so existing imports from api.service still work
export type {
  Account,
  ActiveSignal,
  BacktestResult,
  BacktestStatus,
  BacktestTrade,
  Bar,
  Corridor,
  DbTrade,
  EngineStatus,
  Fractal,
  Portfolio,
  Position,
  Trade,
  SwarmServiceStatus,
  RunnerStatusUI,
  RunnerHeartbeat,
};

// ── RoboApi Service ──

@Injectable({ providedIn: 'root' })
export class RoboApiService {
  private http: HttpClient = inject(HttpClient);
  private base = environment.API_URL;

  private async get<T>(endpoint: string, params: Record<string, string | number | boolean> = {}): Promise<T> {
    let httpParams = new HttpParams();
    Object.keys(params).forEach(k => {
      const v = params[k];
      if (v !== null && v !== undefined) httpParams = httpParams.set(k, String(v));
    });
    return firstValueFrom(this.http.get<T>(`${this.base}/${endpoint}`, { params: httpParams }).pipe(timeout(environment.HTTP_TIMEOUT_MS || 10000)));
  }

  private async put<T>(endpoint: string, body: any): Promise<T> {
    return firstValueFrom(this.http.put<T>(`${this.base}/${endpoint}`, body).pipe(timeout(environment.HTTP_TIMEOUT_MS || 10000)));
  }

  private async post<T>(endpoint: string, body: any): Promise<T> {
    return firstValueFrom(this.http.post<T>(`${this.base}/${endpoint}`, body).pipe(timeout(environment.HTTP_LONG_TIMEOUT_MS || 60000)));
  }

  private async patch<T>(endpoint: string, body: any): Promise<T> {
    return firstValueFrom(this.http.patch<T>(`${this.base}/${endpoint}`, body).pipe(timeout(environment.HTTP_TIMEOUT_MS || 10000)));
  }

  // ── Engines ──

  getEngines(): Promise<EngineStatus[]> {
    return this.get<EngineStatus[]>('engines');
  }

  getEngine(pair: string): Promise<EngineStatus> {
    return this.get<EngineStatus>(`engines/${pair}`);
  }

  getEngineLogs(pair: string): Promise<EngineLog[]> {
    return this.get<EngineLog[]>(`engines/${pair}/logs`);
  }

  controlEngine(pair: string, action: 'pause' | 'resume' | 'emergency-close'): Promise<any> {
    return this.post<any>(`engines/${pair}/control/${action}`, {});
  }

  getPortfolio(): Promise<Portfolio> {
    return this.get<Portfolio>('engines/portfolio');
  }

  // ── Account-scoped Engines ──

  getAccountEngines(accountId: string): Promise<EngineStatus[]> {
    return this.get<EngineStatus[]>(`accounts/${accountId}/engines`);
  }

  getAccountEngine(accountId: string, pair: string): Promise<EngineStatus> {
    return this.get<EngineStatus>(`accounts/${accountId}/engines/${pair}`);
  }

  getAccountPortfolio(accountId: string): Promise<Portfolio> {
    return this.get<Portfolio>(`accounts/${accountId}/engines/portfolio`);
  }

  getAccountEngineLogs(accountId: string, pair: string): Promise<EngineLog[]> {
    return this.get<EngineLog[]>(`accounts/${accountId}/engines/${pair}/logs`);
  }

  controlAccountEngine(accountId: string, pair: string, action: 'pause' | 'resume' | 'emergency-close'): Promise<any> {
    return this.post<any>(`accounts/${accountId}/engines/${pair}/control/${action}`, {});
  }

  // ── B-1 MULTI-TF SELECTOR: historical OHLC bars by symbol+timeframe ──
  getBars(params: {
    symbol: string;
    timeframe: string;
    limit?: number;
    from?: number;
    to?: number;
  }): Promise<{ symbol: string; timeframe: string; count: number; bars: Array<{ time: number; open: number; high: number; low: number; close: number; volume?: number }> }> {
    const q: Record<string, string | number> = {
      symbol: params.symbol,
      timeframe: params.timeframe,
    };
    if (params.limit != null) q['limit'] = params.limit;
    if (params.from != null) q['from'] = params.from;
    if (params.to != null) q['to'] = params.to;
    return this.get('bars', q);
  }

  // ── Accounts ──

  getAccounts(): Promise<Account[]> {
    return this.get<Account[]>('accounts');
  }

  getAccount(id: string): Promise<Account> {
    return this.get<Account>(`accounts/${id}`);
  }

  updateAccount(id: string, body: Partial<Account>): Promise<Account> {
    return this.put<Account>(`auth/accounts/${id}`, body);
  }

  getGlobalSummary(): Promise<AccountSummary[]> {
    return this.get<AccountSummary[]>('accounts/summary');
  }

  // ── Trades ──

  getTrades(params: Record<string, string | number | boolean> = {}): Promise<DbTrade[]> {
    return this.get<DbTrade[]>('trades', params);
  }

  getAccountTrades(accountId: string, params: Record<string, string | number | boolean> = {}): Promise<DbTrade[]> {
    return this.get<DbTrade[]>(`accounts/${accountId}/trades`, params);
  }

  patchTradeJournal(tradeId: string, data: { journal_tags?: string[]; journal_notes?: string }): Promise<DbTrade> {
    return this.patch<DbTrade>(`trades/${tradeId}/journal`, data);
  }

  getTradeQualityCheck(tradeId: string): Promise<any> {
    return this.get<any>(`trades/${tradeId}/quality-check`);
  }

  // ── Analytics ──

  getAnalyticsStats(params: Record<string, string | number | boolean> = {}): Promise<any> {
    return this.get<any>('analytics/stats', params);
  }

  getAccountStats(accountId: string, params: Record<string, string | number | boolean> = {}): Promise<any> {
    return this.get<any>(`accounts/${accountId}/stats`, params);
  }

  getEquityCurve(params: Record<string, string | number | boolean> = {}): Promise<any[]> {
    return this.get<any[]>('analytics/equity', params);
  }

  getAccountEquity(accountId: string, params: Record<string, string | number | boolean> = {}): Promise<any[]> {
    return this.get<any[]>(`accounts/${accountId}/equity`, params);
  }

  getAccountDailyPnl(accountId: string, period?: string): Promise<any[]> {
    const params: Record<string, string> = {};
    if (period) params['period'] = period;
    return this.get<any[]>(`accounts/${accountId}/daily-pnl`, params);
  }

  getAccountHeartbeats(accountId: string): Promise<any[]> {
    return this.get<any[]>(`accounts/${accountId}/heartbeats`);
  }

  // ── Account Deposits/Withdrawals ──

  getAccountBalanceHistory(accountId: string, params: Record<string, string> = {}): Promise<any[]> {
    return this.get<any[]>(`accounts/${accountId}/balance-history`, params);
  }

  getAccountDepositsWithdrawals(accountId: string, params: Record<string, string> = {}): Promise<any[]> {
    return this.get<any[]>(`accounts/${accountId}/deposits-withdrawals`, params);
  }

  getAccountAdjustedPerformance(accountId: string, params: Record<string, string> = {}): Promise<any> {
    return this.get<any>(`accounts/${accountId}/performance-adjusted`, params);
  }

  getAccountSummaryWithTransactions(accountId: string): Promise<any> {
    return this.get<any>(`accounts/${accountId}/summary`);
  }

  // ── Swarm Infrastructure ──

  getSwarmStatus(): Promise<SwarmServiceStatus[]> {
    return this.get<SwarmServiceStatus[]>('swarm/status');
  }

  restartSwarmService(serviceName: string): Promise<any> {
    return this.post<any>(`swarm/restart/${serviceName}`, {});
  }

  // ── Backtest ──

  triggerBacktest(pair: string, body: { bars?: number; initialBalance?: number; spreadPips?: number; overrides?: Record<string, any> } = {}): Promise<any> {
    return this.post<any>(`backtest/${pair}`, body);
  }

  getBacktestStatus(pair: string): Promise<BacktestStatus> {
    return this.get<BacktestStatus>(`backtest/${pair}/status`);
  }

  triggerReplay(pair: string, days: number): Promise<any> {
    return this.post<any>(`backtest/${pair}/replay`, { days });
  }

  getReplayStatus(pair: string): Promise<BacktestStatus> {
    return this.get<BacktestStatus>(`backtest/${pair}/replay/status`);
  }

  simulate(pair: string, days: number): Promise<any> {
    return this.post<any>(`backtest/${pair}/simulate`, { days });
  }

  simulateRange(pair: string, fromMs: number, toMs: number): Promise<any> {
    return this.post<any>(`backtest/${pair}/simulate`, { fromMs, toMs });
  }

  engineSimulate(pair: string, days: number): Promise<any> {
    return this.post<any>(`backtest/${pair}/engine-simulate`, { days });
  }

  engineSimulateRange(pair: string, fromMs: number, toMs: number): Promise<any> {
    return this.post<any>(`backtest/${pair}/engine-simulate`, { fromTime: fromMs, toTime: toMs });
  }

  // ── Config ──

  getConfigs(): Promise<any[]> {
    return this.get<any[]>('config');
  }

  getConfig(preset: string): Promise<any> {
    return this.get<any>(`config/${preset}`);
  }

  updateConfig(preset: string, config: Record<string, unknown>): Promise<any> {
    return this.put<any>(`config/${preset}`, config);
  }

  // ── Auth ──

  login(email: string, password: string): Promise<{ token: string; employee: any }> {
    return this.post<{ token: string; employee: any }>('auth/login', { email, password });
  }

  getMe(): Promise<any> {
    return this.get<any>('auth/me');
  }

  getMyAccounts(): Promise<any[]> {
    return this.get<any[]>('auth/accounts');
  }

  // ── Audit Trail ──

  getAccountAuditTrail(accountId: string, params: Record<string, string | number> = {}): Promise<any[]> {
    return this.get<any[]>(`accounts/${accountId}/audit`, params);
  }

  getAccountAuditEventTypes(accountId: string): Promise<string[]> {
    return this.get<string[]>(`accounts/${accountId}/audit/event-types`);
  }

  // ── Pattern Intelligence ──

  getPiHealth(): Promise<PatternIntelligenceHealth> {
    return this.get<PatternIntelligenceHealth>('pattern-intelligence/health');
  }

  getPiStats(): Promise<PatternIntelligenceDbStats> {
    return this.get<PatternIntelligenceDbStats>('pattern-intelligence/stats');
  }

  getMyAccountsWithToken(token: string): Promise<any[]> {
    return firstValueFrom(
      this.http.get<any[]>(`${this.base}/auth/accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      }).pipe(timeout(environment.HTTP_TIMEOUT_MS || 10000)),
    ) as Promise<any[]>;
  }
}
